// ─── Motor Financiero Único — PrestaMass ─────────────────────────────────────
// Motor de cálculo compartido por Simulador, Nuevo Préstamo, Calendario,
// Pagos, Refinanciamiento, Proyecciones, Reportes y Dashboard.
// REGLA: Cada sistema tiene su propia lógica. No mezclar fórmulas.

export type SistemaAmortizacion =
  | 'interes-simple-saldo'      // A. Interés simple sobre saldo
  | 'banca-cuota-fija'          // B. Banca formal — Cuota fija (sistema francés)
  | 'banca-amortizacion-fija'   // C. Banca formal — Amortización fija (sistema alemán)
  | 'bullet';                   // D. Bullet — Interés periódico, capital al final

export type TipoInteresSim =
  | 'sobre-saldo'
  | 'simple' |'cuota-fija' |'amortizacion-fija' |'bullet';

// IMPORTANTE: TipoPeriodo define la unidad de la tasa ingresada por el usuario.
// 'por-periodo' = la tasa ya está expresada por período de cobro (usar directamente).
// 'semanal' = la tasa es semanal (usar directamente si frecuencia es Semanal).
// 'mensual' = la tasa es mensual (usar directamente si frecuencia es Mensual).
// 'anual' = la tasa es anual (dividir entre períodos del año).
export type TipoPeriodo = 'por-periodo' | 'semanal' | 'quincenal' | 'mensual' | 'anual';
export type FrecuenciaSimulador = 'Diario' | 'Semanal' | 'Quincenal' | 'Mensual' | 'Personalizado';

export interface FilaAmortizacion {
  numero: number;
  fecha: string;
  saldoInicial: number;
  capital: number;
  interes: number;
  servicioAdmin: number;
  mora: number;
  multa: number;
  total: number;
  saldoFinal: number;
  capitalPendiente?: number; // Solo para sistema Bullet
}

export interface ResultadoSimulacion {
  sistema: SistemaAmortizacion;
  tipoInteres: TipoInteresSim;
  capitalTotal: number;
  interesTotal: number;
  servicioAdminTotal: number;
  totalCobrar: number;
  primeraCuota: number;
  ultimaCuota: number;
  promedioCuota: number;
  fechaInicial: string;
  fechaFinal: string;
  filas: FilaAmortizacion[];
  validacion?: ValidacionCalculo;
}

export interface ParametrosSimulacion {
  monto: number;
  tasa: number;
  tipoPeriodo: TipoPeriodo;
  cuotas: number;
  frecuencia: FrecuenciaSimulador;
  diaCobro: string;
  fechaInicio: string; // ISO yyyy-mm-dd
  sistema: SistemaAmortizacion;
  tipoInteres: TipoInteresSim;
  servicioAdmin: number;
  multa: number;
}

export interface ValidacionCalculo {
  valido: boolean;
  errores: string[];
  advertencias: string[];
}

// ─── Redondeo financiero ──────────────────────────────────────────────────────
// Máximo 2 decimales. Ajuste de centavos en última cuota.
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── Helpers de fecha ─────────────────────────────────────────────────────────

const DIA_NUM: Record<string, number> = {
  Domingo: 0, Lunes: 1, Martes: 2, 'Miércoles': 3,
  Jueves: 4, Viernes: 5, Sábado: 6,
};

function isoToDisplay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function addDaysToISO(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function addMonthsToISO(iso: string, months: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

function getNextWeekday(fromISO: string, diaSemana: string): string {
  const d = new Date(fromISO + 'T00:00:00');
  const target = DIA_NUM[diaSemana] ?? 4;
  let diff = target - d.getDay();
  if (diff <= 0) diff += 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export function generarFechas(
  fechaInicioISO: string,
  frecuencia: FrecuenciaSimulador,
  diaCobro: string,
  cuotas: number
): string[] {
  const fechas: string[] = [];

  // Primera fecha de pago
  let primerPagoISO: string;
  if (frecuencia === 'Semanal' && diaCobro && DIA_NUM[diaCobro] !== undefined) {
    primerPagoISO = getNextWeekday(fechaInicioISO, diaCobro);
  } else {
    const dias = frecuencia === 'Diario' ? 1
      : frecuencia === 'Semanal' ? 7
      : frecuencia === 'Quincenal' ? 15
      : 30;
    primerPagoISO = addDaysToISO(fechaInicioISO, dias);
  }

  fechas.push(isoToDisplay(primerPagoISO));

  for (let i = 1; i < cuotas; i++) {
    const prevISO = fechas[i - 1].split('/').reverse().join('-');
    let nextISO: string;
    switch (frecuencia) {
      case 'Diario':     nextISO = addDaysToISO(prevISO, 1); break;
      case 'Semanal':    nextISO = addDaysToISO(prevISO, 7); break;
      case 'Quincenal':  nextISO = addDaysToISO(prevISO, 15); break;
      case 'Mensual':    nextISO = addMonthsToISO(prevISO, 1); break;
      default:           nextISO = addDaysToISO(prevISO, 7);
    }
    fechas.push(isoToDisplay(nextISO));
  }

  return fechas;
}

// ─── TASA POR PERÍODO ─────────────────────────────────────────────────────────
// REGLA FUNDAMENTAL:
// Si el usuario selecciona "9% semanal" con frecuencia Semanal → usar 9% directamente.
// Si el usuario selecciona "9% mensual" con frecuencia Semanal → convertir.
// Si el usuario selecciona "por período" → usar directamente sin conversión.
//
// Fórmulas básicas:
//   I = P × r × t  (interés simple)
//   Porcentaje = (Interés ÷ Capital) × 100
//   Interés = Capital × Tasa ÷ 100
//   Capital = Interés × 100 ÷ Tasa

export function tasaPorPeriodo(
  tasa: number,
  tipoPeriodo: TipoPeriodo,
  frecuencia: FrecuenciaSimulador
): number {
  const t = tasa / 100;

  // 'por-periodo': la tasa ya está expresada por período de cobro → usar directamente
  if (tipoPeriodo === 'por-periodo') return t;

  // Si la unidad de la tasa coincide con la frecuencia → usar directamente
  // Ejemplo: 9% semanal con frecuencia Semanal = 9% por cuota
  if (tipoPeriodo === 'semanal' && frecuencia === 'Semanal') return t;
  if (tipoPeriodo === 'quincenal' && frecuencia === 'Quincenal') return t;
  if (tipoPeriodo === 'mensual' && frecuencia === 'Mensual') return t;

  // Conversión: primero a anual, luego al período de cobro
  let tasaAnual: number;
  switch (tipoPeriodo) {
    case 'semanal':    tasaAnual = t * 52; break;
    case 'quincenal':  tasaAnual = t * 26; break;
    case 'mensual':    tasaAnual = t * 12; break;
    case 'anual':      tasaAnual = t; break;
    default:           tasaAnual = t * 52;
  }

  switch (frecuencia) {
    case 'Diario':     return tasaAnual / 365;
    case 'Semanal':    return tasaAnual / 52;
    case 'Quincenal':  return tasaAnual / 26;
    case 'Mensual':    return tasaAnual / 12;
    default:           return tasaAnual / 52;
  }
}

// ─── A. INTERÉS SIMPLE SOBRE SALDO ───────────────────────────────────────────
// Interés = Saldo pendiente × tasa por período
// Capital se reduce en cada cuota. Interés decrece conforme baja el saldo.
// Capital por cuota = Capital inicial ÷ número de cuotas (fijo)
// Cuota total = Capital fijo + Interés variable + Servicio

function calcularInteresSimpleSaldo(p: ParametrosSimulacion, fechas: string[]): FilaAmortizacion[] {
  const tp = tasaPorPeriodo(p.tasa, p.tipoPeriodo, p.frecuencia);
  const capitalFijo = r2(p.monto / p.cuotas);
  let filas: FilaAmortizacion[] = [];
  let saldo = p.monto;

  for (let i = 0; i < p.cuotas; i++) {
    const saldoInicial = r2(saldo);
    const interes = r2(saldoInicial * tp);
    // Última cuota: ajustar capital para que saldo quede exactamente 0
    const capital = i === p.cuotas - 1 ? r2(saldo) : capitalFijo;
    const total = r2(capital + interes + p.servicioAdmin);
    saldo = r2(Math.max(0, saldo - capital));

    filas.push({
      numero: i + 1,
      fecha: fechas[i] || '',
      saldoInicial,
      capital,
      interes,
      servicioAdmin: p.servicioAdmin,
      mora: 0,
      multa: 0,
      total,
      saldoFinal: saldo,
    });
  }
  return filas;
}

// ─── B. BANCA FORMAL — CUOTA FIJA (Sistema Francés) ──────────────────────────
// Fórmula PMT: Cuota = P × [r(1+r)^n] ÷ [(1+r)^n - 1]
// La cuota capital+interés es constante. Al inicio más interés, al final más capital.
// Servicio administrativo se agrega independientemente.

function calcularBancaCuotaFija(p: ParametrosSimulacion, fechas: string[]): FilaAmortizacion[] {
  const tp = tasaPorPeriodo(p.tasa, p.tipoPeriodo, p.frecuencia);
  let filas: FilaAmortizacion[] = [];

  // PMT = P × r(1+r)^n / [(1+r)^n - 1]
  let cuotaBase: number;
  if (tp === 0) {
    cuotaBase = r2(p.monto / p.cuotas);
  } else {
    const factor = Math.pow(1 + tp, p.cuotas);
    cuotaBase = r2(p.monto * (tp * factor) / (factor - 1));
  }

  let saldo = p.monto;

  for (let i = 0; i < p.cuotas; i++) {
    const saldoInicial = r2(saldo);
    const interes = r2(saldoInicial * tp);
    // Última cuota: ajustar para que saldo quede exactamente 0
    const capital = i === p.cuotas - 1 ? r2(saldo) : r2(cuotaBase - interes);
    const total = r2(capital + interes + p.servicioAdmin);
    saldo = r2(Math.max(0, saldo - capital));

    filas.push({
      numero: i + 1,
      fecha: fechas[i] || '',
      saldoInicial,
      capital,
      interes,
      servicioAdmin: p.servicioAdmin,
      mora: 0,
      multa: 0,
      total,
      saldoFinal: saldo,
    });
  }
  return filas;
}

// ─── C. BANCA FORMAL — AMORTIZACIÓN FIJA (Sistema Alemán) ────────────────────
// Capital por cuota = Capital inicial ÷ número de cuotas (constante)
// Interés = Saldo pendiente × tasa por período (decrece)
// Cuota total = Capital fijo + Interés variable + Servicio (decrece progresivamente)

function calcularBancaAmortizacionFija(p: ParametrosSimulacion, fechas: string[]): FilaAmortizacion[] {
  const tp = tasaPorPeriodo(p.tasa, p.tipoPeriodo, p.frecuencia);
  const capitalFijo = r2(p.monto / p.cuotas);
  let filas: FilaAmortizacion[] = [];
  let saldo = p.monto;

  for (let i = 0; i < p.cuotas; i++) {
    const saldoInicial = r2(saldo);
    const interes = r2(saldoInicial * tp);
    // Última cuota: ajustar capital para que saldo quede exactamente 0
    const capital = i === p.cuotas - 1 ? r2(saldo) : capitalFijo;
    const total = r2(capital + interes + p.servicioAdmin);
    saldo = r2(Math.max(0, saldo - capital));

    filas.push({
      numero: i + 1,
      fecha: fechas[i] || '',
      saldoInicial,
      capital,
      interes,
      servicioAdmin: p.servicioAdmin,
      mora: 0,
      multa: 0,
      total,
      saldoFinal: saldo,
    });
  }
  return filas;
}

// ─── D. BULLET — INTERÉS PERIÓDICO Y CAPITAL AL FINAL ────────────────────────
// REGLA FUNDAMENTAL:
//   - El capital NO se amortiza en las cuotas intermedias.
//   - Capital pendiente = Capital original (hasta la última cuota).
//   - Cada cuota intermedia paga SOLO: Interés + Servicio.
//   - La ÚLTIMA cuota paga: Capital + Interés + Servicio.
//
// Interés por período = Capital × tasa por período (constante en todas las cuotas)
//
// EJEMPLO OBLIGATORIO (Prueba 1):
//   Capital: Lps 10,000 | Tasa: 9% semanal | 3 cuotas | Servicio: Lps 60
//   Cuota 1: Capital=0, Interés=900, Servicio=60, Total=960, Saldo=10,000
//   Cuota 2: Capital=0, Interés=900, Servicio=60, Total=960, Saldo=10,000
//   Cuota 3: Capital=10,000, Interés=900, Servicio=60, Total=10,960, Saldo=0
//   Totales: Capital=10,000, Intereses=2,700, Servicios=180, Total=12,880

function calcularBullet(p: ParametrosSimulacion, fechas: string[]): FilaAmortizacion[] {
  const tp = tasaPorPeriodo(p.tasa, p.tipoPeriodo, p.frecuencia);
  // Interés periódico = Capital × tasa por período (constante, calculado sobre capital original)
  const interesPeriodico = r2(p.monto * tp);
  let filas: FilaAmortizacion[] = [];

  for (let i = 0; i < p.cuotas; i++) {
    const esUltima = i === p.cuotas - 1;
    // Cuotas intermedias: capital = 0. Última cuota: capital = capital original completo.
    const capital = esUltima ? p.monto : 0;
    const interes = interesPeriodico;
    const total = r2(capital + interes + p.servicioAdmin);
    // Saldo = capital pendiente. En intermedias = capital original. En última = 0.
    const capitalPendiente = esUltima ? 0 : p.monto;

    filas.push({
      numero: i + 1,
      fecha: fechas[i] || '',
      saldoInicial: p.monto,
      capital,
      interes,
      servicioAdmin: p.servicioAdmin,
      mora: 0,
      multa: 0,
      total,
      saldoFinal: capitalPendiente,
      capitalPendiente,
    });
  }
  return filas;
}

// ─── VALIDACIÓN DE CONSISTENCIA ──────────────────────────────────────────────
// Verifica que los cálculos sean consistentes antes de guardar un préstamo.
// Si alguna validación falla, NO se debe permitir guardar el préstamo.

export function validarCalculo(
  p: ParametrosSimulacion,
  filas: FilaAmortizacion[]
): ValidacionCalculo {
  const errores: string[] = [];
  const advertencias: string[] = [];

  if (filas.length === 0) {
    errores.push('No se generaron cuotas.');
    return { valido: false, errores, advertencias };
  }

  // 1. Capital inicial coincide
  if (p.monto <= 0) errores.push('El capital inicial debe ser mayor a 0.');

  // 2. Suma del capital de las cuotas = capital prestado
  const sumaCapital = r2(filas.reduce((s, f) => s + f.capital, 0));
  if (Math.abs(sumaCapital - p.monto) > 0.02) {
    errores.push(`Suma de capital (${sumaCapital}) ≠ capital prestado (${p.monto}). Diferencia: ${r2(Math.abs(sumaCapital - p.monto))}.`);
  }

  // 3. Suma de intereses = interés total
  const sumaIntereses = r2(filas.reduce((s, f) => s + f.interes, 0));
  const interesTotal = r2(p.monto * tasaPorPeriodo(p.tasa, p.tipoPeriodo, p.frecuencia) * p.cuotas);
  // Para sistemas no-bullet, el interés total puede variar (sobre saldo decrece)
  // Solo verificar que la suma de cuotas coincida con el resumen
  const interesTotalFilas = sumaIntereses;
  if (interesTotalFilas < 0) {
    errores.push('El interés total no puede ser negativo.');
  }

  // 4. Suma de servicios = servicio configurado × cuotas
  const sumaServicios = r2(filas.reduce((s, f) => s + f.servicioAdmin, 0));
  const servicioEsperado = r2(p.servicioAdmin * p.cuotas);
  if (Math.abs(sumaServicios - servicioEsperado) > 0.02) {
    advertencias.push(`Suma de servicios (${sumaServicios}) ≠ servicio esperado (${servicioEsperado}).`);
  }

  // 5. Saldo final = 0
  const ultimaFila = filas[filas.length - 1];
  const saldoFinal = p.sistema === 'bullet' ? (ultimaFila.capitalPendiente ?? 0) : ultimaFila.saldoFinal;
  if (Math.abs(saldoFinal) > 0.02) {
    errores.push(`El saldo final debe ser Lps 0. Saldo actual: ${saldoFinal}.`);
  }

  // 6. Número de cuotas coincide
  if (filas.length !== p.cuotas) {
    errores.push(`Número de cuotas generadas (${filas.length}) ≠ cuotas configuradas (${p.cuotas}).`);
  }

  // 7. Bullet: verificar que cuotas intermedias no tengan capital
  if (p.sistema === 'bullet') {
    const cuotasIntermediasConCapital = filas.slice(0, -1).filter(f => f.capital > 0);
    if (cuotasIntermediasConCapital.length > 0) {
      errores.push(`Sistema Bullet: las cuotas intermedias no deben tener capital. Cuotas con error: ${cuotasIntermediasConCapital.map(f => f.numero).join(', ')}.`);
    }
    // Última cuota debe tener el capital completo
    if (Math.abs(ultimaFila.capital - p.monto) > 0.02) {
      errores.push(`Sistema Bullet: la última cuota debe tener el capital completo (${p.monto}). Capital actual: ${ultimaFila.capital}.`);
    }
  }

  // 8. No cuotas duplicadas
  const numeros = filas.map(f => f.numero);
  const duplicados = numeros.filter((n, i) => numeros.indexOf(n) !== i);
  if (duplicados.length > 0) {
    errores.push(`Cuotas duplicadas: ${duplicados.join(', ')}.`);
  }

  // 9. Total de cuotas = capital + intereses + servicios
  const totalEsperado = r2(p.monto + interesTotalFilas + sumaServicios);
  const totalFilas = r2(filas.reduce((s, f) => s + f.total, 0));
  if (Math.abs(totalFilas - totalEsperado) > 0.05) {
    advertencias.push(`Total de cuotas (${totalFilas}) difiere del esperado (${totalEsperado}).`);
  }

  return {
    valido: errores.length === 0,
    errores,
    advertencias,
  };
}

// ─── FUNCIÓN PRINCIPAL DE SIMULACIÓN ─────────────────────────────────────────
// Motor único utilizado por: Simulador, Nuevo Préstamo, Calendario, Pagos,
// Refinanciamiento, Proyecciones, Reportes y Dashboard.

export function simular(p: ParametrosSimulacion): ResultadoSimulacion {
  if (p.monto <= 0 || p.cuotas <= 0) {
    return {
      sistema: p.sistema,
      tipoInteres: p.tipoInteres,
      capitalTotal: 0,
      interesTotal: 0,
      servicioAdminTotal: 0,
      totalCobrar: 0,
      primeraCuota: 0,
      ultimaCuota: 0,
      promedioCuota: 0,
      fechaInicial: '',
      fechaFinal: '',
      filas: [],
    };
  }

  const fechas = generarFechas(p.fechaInicio, p.frecuencia, p.diaCobro, p.cuotas);

  let filas: FilaAmortizacion[];
  switch (p.sistema) {
    case 'interes-simple-saldo':
      filas = calcularInteresSimpleSaldo(p, fechas);
      break;
    case 'banca-cuota-fija':
      filas = calcularBancaCuotaFija(p, fechas);
      break;
    case 'banca-amortizacion-fija':
      filas = calcularBancaAmortizacionFija(p, fechas);
      break;
    case 'bullet':
      filas = calcularBullet(p, fechas);
      break;
    default:
      filas = calcularInteresSimpleSaldo(p, fechas);
  }

  // Calcular totales desde las filas (fuente única de verdad)
  const interesTotal = r2(filas.reduce((s, f) => s + f.interes, 0));
  const servicioAdminTotal = r2(filas.reduce((s, f) => s + f.servicioAdmin, 0));
  const capitalTotal = r2(filas.reduce((s, f) => s + f.capital, 0));
  const totalCobrar = r2(capitalTotal + interesTotal + servicioAdminTotal);
  const totales = filas.map(f => f.total);
  const primeraCuota = totales[0] ?? 0;
  const ultimaCuota = totales[totales.length - 1] ?? 0;
  const promedioCuota = r2(totalCobrar / p.cuotas);

  // Validación automática
  const validacion = validarCalculo(p, filas);

  return {
    sistema: p.sistema,
    tipoInteres: p.tipoInteres,
    capitalTotal,
    interesTotal,
    servicioAdminTotal,
    totalCobrar,
    primeraCuota,
    ultimaCuota,
    promedioCuota,
    fechaInicial: fechas[0] ?? '',
    fechaFinal: fechas[fechas.length - 1] ?? '',
    filas,
    validacion,
  };
}

// ─── PRUEBAS AUTOMÁTICAS ──────────────────────────────────────────────────────
// Verificar que el motor produce los resultados correctos.

export interface ResultadoPrueba {
  nombre: string;
  aprobada: boolean;
  esperado: string;
  obtenido: string;
  detalle?: string;
}

export function ejecutarPruebas(): ResultadoPrueba[] {
  const pruebas: ResultadoPrueba[] = [];

  // PRUEBA 1: Bullet — Lps 10,000 / 9% semanal / 3 cuotas / Servicio Lps 60
  // Resultado esperado: Cuotas 1-2 = Lps 960, Cuota 3 = Lps 10,960, Total = Lps 12,880
  const p1 = simular({
    monto: 10000,
    tasa: 9,
    tipoPeriodo: 'semanal',
    cuotas: 3,
    frecuencia: 'Semanal',
    diaCobro: 'Jueves',
    fechaInicio: '2026-08-01',
    sistema: 'bullet',
    tipoInteres: 'bullet',
    servicioAdmin: 60,
    multa: 0,
  });

  const p1_cuota1_ok = p1.filas[0]?.capital === 0 && p1.filas[0]?.interes === 900 && p1.filas[0]?.total === 960;
  const p1_cuota2_ok = p1.filas[1]?.capital === 0 && p1.filas[1]?.interes === 900 && p1.filas[1]?.total === 960;
  const p1_cuota3_ok = p1.filas[2]?.capital === 10000 && p1.filas[2]?.interes === 900 && p1.filas[2]?.total === 10960;
  const p1_total_ok = p1.totalCobrar === 12880 && p1.interesTotal === 2700 && p1.servicioAdminTotal === 180;

  pruebas.push({
    nombre: 'Bullet: Lps 10,000 / 9% semanal / 3 cuotas / Serv. Lps 60',
    aprobada: p1_cuota1_ok && p1_cuota2_ok && p1_cuota3_ok && p1_total_ok,
    esperado: 'C1=960, C2=960, C3=10,960 | Interés=2,700 | Total=12,880',
    obtenido: `C1=${p1.filas[0]?.total}, C2=${p1.filas[1]?.total}, C3=${p1.filas[2]?.total} | Interés=${p1.interesTotal} | Total=${p1.totalCobrar}`,
    detalle: !p1_cuota1_ok ? `Cuota 1: Capital=${p1.filas[0]?.capital}, Interés=${p1.filas[0]?.interes}, Total=${p1.filas[0]?.total}` : undefined,
  });

  // PRUEBA 2: Bullet — Lps 20,000 / 5% / 4 cuotas
  // Interés por cuota: 1,000. C1=C2=C3=1,000, C4=21,000. Total=24,000
  const p2 = simular({
    monto: 20000,
    tasa: 5,
    tipoPeriodo: 'semanal',
    cuotas: 4,
    frecuencia: 'Semanal',
    diaCobro: 'Jueves',
    fechaInicio: '2026-08-01',
    sistema: 'bullet',
    tipoInteres: 'bullet',
    servicioAdmin: 0,
    multa: 0,
  });

  const p2_ok = p2.filas[0]?.total === 1000 && p2.filas[1]?.total === 1000
    && p2.filas[2]?.total === 1000 && p2.filas[3]?.total === 21000
    && p2.totalCobrar === 24000 && p2.interesTotal === 4000;

  pruebas.push({
    nombre: 'Bullet: Lps 20,000 / 5% semanal / 4 cuotas / Sin servicio',
    aprobada: p2_ok,
    esperado: 'C1=C2=C3=1,000, C4=21,000 | Interés=4,000 | Total=24,000',
    obtenido: `C1=${p2.filas[0]?.total}, C2=${p2.filas[1]?.total}, C3=${p2.filas[2]?.total}, C4=${p2.filas[3]?.total} | Interés=${p2.interesTotal} | Total=${p2.totalCobrar}`,
  });

  // PRUEBA 3: Interés simple — I = P × r × t
  // Capital: 10,000 / Tasa: 9% semanal / 3 semanas
  // Interés total = 10,000 × 0.09 × 3 = 2,700
  const p3 = simular({
    monto: 10000,
    tasa: 9,
    tipoPeriodo: 'semanal',
    cuotas: 3,
    frecuencia: 'Semanal',
    diaCobro: 'Jueves',
    fechaInicio: '2026-08-01',
    sistema: 'interes-simple-saldo',
    tipoInteres: 'sobre-saldo',
    servicioAdmin: 0,
    multa: 0,
  });

  // Para interés sobre saldo: interés decrece, no es exactamente P×r×t
  // Verificar que el primer interés sea correcto
  const p3_ok = p3.filas[0]?.interes === 900;

  pruebas.push({
    nombre: 'Interés Simple sobre Saldo: primer interés = Capital × tasa',
    aprobada: p3_ok,
    esperado: 'Primer interés = 900 (10,000 × 9%)',
    obtenido: `Primer interés = ${p3.filas[0]?.interes}`,
  });

  return pruebas;
}

// ─── Comparación entre sistemas ───────────────────────────────────────────────

export interface ComparacionItem {
  sistema: SistemaAmortizacion;
  nombre: string;
  primeraCuota: number;
  ultimaCuota: number;
  promedioCuota: number;
  interesTotal: number;
  totalCobrar: number;
}

export function compararSistemas(p: Omit<ParametrosSimulacion, 'sistema' | 'tipoInteres'>): ComparacionItem[] {
  const sistemas: Array<{ sistema: SistemaAmortizacion; tipoInteres: TipoInteresSim }> = [
    { sistema: 'interes-simple-saldo', tipoInteres: 'sobre-saldo' },
    { sistema: 'banca-cuota-fija', tipoInteres: 'cuota-fija' },
    { sistema: 'banca-amortizacion-fija', tipoInteres: 'amortizacion-fija' },
    { sistema: 'bullet', tipoInteres: 'bullet' },
  ];

  return sistemas.map(s => {
    const res = simular({ ...p, sistema: s.sistema, tipoInteres: s.tipoInteres });
    return {
      sistema: s.sistema,
      nombre: SISTEMAS_INFO[s.sistema].nombre,
      primeraCuota: res.primeraCuota,
      ultimaCuota: res.ultimaCuota,
      promedioCuota: res.promedioCuota,
      interesTotal: res.interesTotal,
      totalCobrar: res.totalCobrar,
    };
  });
}

// ─── Metadatos de sistemas ────────────────────────────────────────────────────

export const SISTEMAS_INFO: Record<SistemaAmortizacion, { nombre: string; descripcion: string; tipoInteres: TipoInteresSim }> = {
  'interes-simple-saldo': {
    nombre: 'Interés simple sobre saldo',
    descripcion: 'El interés se calcula sobre el saldo pendiente. Capital fijo, interés decrece.',
    tipoInteres: 'sobre-saldo',
  },
  'banca-cuota-fija': {
    nombre: 'Banca formal — Cuota fija',
    descripcion: 'Cuota constante. Al inicio más interés, al final más capital (sistema francés PMT).',
    tipoInteres: 'cuota-fija',
  },
  'banca-amortizacion-fija': {
    nombre: 'Banca formal — Amortización fija',
    descripcion: 'Capital fijo por cuota. Interés decrece. Cuota total disminuye progresivamente.',
    tipoInteres: 'amortizacion-fija',
  },
  'bullet': {
    nombre: 'Bullet — Interés primero y capital al final',
    descripcion: 'Solo intereses durante el período. Capital completo al vencimiento.',
    tipoInteres: 'bullet',
  },
};

export const TIPOS_INTERES_INFO: Record<TipoInteresSim, { nombre: string; descripcion: string }> = {
  'sobre-saldo': { nombre: 'Interés sobre saldo', descripcion: 'Se calcula sobre el capital pendiente.' },
  'simple': { nombre: 'Interés simple', descripcion: 'Calculado sobre el capital original.' },
  'cuota-fija': { nombre: 'Interés de cuota fija', descripcion: 'Distribuido en cuotas iguales (sistema francés PMT).' },
  'amortizacion-fija': { nombre: 'Interés sobre amortización fija', descripcion: 'Sobre saldo con capital constante.' },
  'bullet': { nombre: 'Interés bullet', descripcion: 'Intereses periódicos constantes, capital al final.' },
};

export const TIPO_PERIODO_LABELS: Record<TipoPeriodo, string> = {
  'por-periodo': 'Por período',
  'semanal': 'Semanal',
  'quincenal': 'Quincenal',
  'mensual': 'Mensual',
  'anual': 'Anual',
};
